/** @jsxImportSource preact */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { ChatApi, Message, WidgetConfig } from './api';
import { getOrCreateSessionId, getStoredInquiryId, setStoredInquiryId, consumeResumeToken } from './session';

interface Props {
  site: string;
  erpUrl: string;
  greeting?: string;
  onlineHours?: string;
  /** 자사몰 로그인 사용자 (Next.js 컴포넌트에서 props 로 전달). */
  user?: { id?: string; name?: string; phone?: string };
}

const POLL_INTERVAL_MS = 5000;
// auto-link URL — http(s) + domain + path
const URL_RE = /\b(https?:\/\/[^\s)]+|www\.[^\s)]+)/gi;
const IMG_RE = /\.(jpe?g|png|gif|webp)(\?|#|$)/i;

export function ChatWidget({ site, erpUrl, greeting, onlineHours, user }: Props) {
  const api = useMemo(() => new ChatApi(erpUrl, site), [erpUrl, site]);
  const sessionId = useMemo(() => getOrCreateSessionId(), []);
  // ERP /config — title / greeting / eta / online_hours 운영 설정
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.getConfig()
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch(() => { /* fallback to defaults */ });
    return () => { cancelled = true; };
  }, [api]);
  const cfg = {
    title: config?.title || '올스틸 상담',
    greeting: config?.greeting || greeting || '확인 후 빠른 시간 내에 답장 드리겠습니다.',
    eta: config?.eta || '예상 답변 시간 — 빠른 시간 내',
    online_hours: config?.online_hours || onlineHours || '평일 09:00–18:00',
  };
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inquiryId, setInquiryId] = useState<string | null>(null);
  const [lastSince, setLastSince] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showIdentify, setShowIdentify] = useState(false);
  const [identified, setIdentified] = useState(false);
  const [identifyName, setIdentifyName] = useState(user?.name || '');
  const [identifyPhone, setIdentifyPhone] = useState(user?.phone || '');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // 이미지 클릭 시 라이트박스 모달
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // ─ 초기화: resume token 우선, 그 다음 저장된 inquiry_id ─
  useEffect(() => {
    (async () => {
      try {
        const token = consumeResumeToken();
        if (token) {
          const r = await api.resume(token);
          setInquiryId(r.inquiry_id);
          setStoredInquiryId(r.inquiry_id);
          setMessages(r.messages);
          setOpen(true);
          if (r.customer_phone) setIdentified(true);
          return;
        }
        const storedId = getStoredInquiryId();
        if (storedId) {
          setInquiryId(storedId);
        }
      } catch (e) {
        console.warn('[chat] init failed', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─ 폴링 ─
  useEffect(() => {
    let timer: number | null = null;
    let cancelled = false;

    async function poll() {
      try {
        const r = await api.pollMessages({
          inquiry_id: inquiryId,
          client_session: sessionId,
          since: lastSince,
        });
        if (cancelled) return;
        if (r.inquiry_id && r.inquiry_id !== inquiryId) {
          setInquiryId(r.inquiry_id);
          setStoredInquiryId(r.inquiry_id);
        }
        if (r.customer_phone) setIdentified(true);
        if (r.messages.length > 0) {
          // 새 메시지 append (id 중복 제거)
          setMessages((prev) => {
            const knownIds = new Set(prev.map((m) => m.id));
            const fresh = r.messages.filter((m) => !knownIds.has(m.id));
            return [...prev, ...fresh];
          });
          const newest = r.messages[r.messages.length - 1].sent_at;
          if (newest) setLastSince(newest);
          // outbound 면 unread ↑ (창 닫혀있을 때만)
          if (!open) {
            const outboundCount = r.messages.filter((m) => m.direction === 'outbound').length;
            if (outboundCount > 0) setUnread((u) => u + outboundCount);
          }
        }
      } catch (e) {
        if (!cancelled) console.warn('[chat] poll error', e);
      }
    }
    poll();
    timer = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [api, inquiryId, sessionId, lastSince, open]);

  // ─ 열릴 때 unread 리셋 + 맨 아래 스크롤 ─
  useEffect(() => {
    if (open) {
      setUnread(0);
      requestAnimationFrame(() => {
        const el = bodyRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
  }, [open, messages.length]);

  // ─ 보내기 ─
  async function handleSend(extra?: { attachment_urls?: string[]; presetText?: string }) {
    const body = (extra?.presetText ?? text).trim();
    if (!body && !(extra?.attachment_urls && extra.attachment_urls.length > 0)) return;
    setError(null);
    try {
      const r = await api.postMessage({
        text: body || undefined,
        attachment_urls: extra?.attachment_urls,
        inquiry_id: inquiryId,
        client_session: sessionId,
        member_id: user?.id,
        member_name: user?.name,
        member_phone: user?.phone,
      });
      if (r.inquiry_id !== inquiryId) {
        setInquiryId(r.inquiry_id);
        setStoredInquiryId(r.inquiry_id);
      }
      if (!extra?.presetText) setText('');
      // 즉시 폴링 1회 (UX)
      const pr = await api.pollMessages({
        inquiry_id: r.inquiry_id,
        client_session: sessionId,
        since: lastSince,
      });
      if (pr.messages.length > 0) {
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const fresh = pr.messages.filter((m) => !ids.has(m.id));
          return [...prev, ...fresh];
        });
        const newest = pr.messages[pr.messages.length - 1].sent_at;
        if (newest) setLastSince(newest);
      }
      // 첫/두번째 메시지 후 식별 폼 노출 (전화/이름 없으면)
      const totalUserMsgs = messages.filter((m) => m.direction === 'inbound').length + 1;
      if (totalUserMsgs >= 1 && !identified && !user?.phone) {
        setShowIdentify(true);
      }
    } catch (e: any) {
      setError(e?.message || '전송 실패');
    }
  }

  async function uploadAndSend(files: File[]) {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const urls: string[] = [];
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        const r = await api.uploadImage(f);
        urls.push(r.url);
      }
      if (urls.length > 0) await handleSend({ attachment_urls: urls });
    } catch (e: any) {
      setError(e?.message || '업로드 실패');
    } finally {
      setUploading(false);
    }
  }

  async function handleFile(ev: Event) {
    const inp = ev.currentTarget as HTMLInputElement;
    if (!inp.files || inp.files.length === 0) return;
    const files = Array.from(inp.files);
    inp.value = '';
    await uploadAndSend(files);
  }

  // 드래그앤드롭 — 채팅창 어디든 이미지 drop 가능
  const [dragOver, setDragOver] = useState(false);
  function handleDragOver(e: DragEvent) {
    if (!e.dataTransfer) return;
    // 이미지가 포함된 경우만 인디케이터 표시
    const hasFile = Array.from(e.dataTransfer.types).includes('Files');
    if (!hasFile) return;
    e.preventDefault();
    setDragOver(true);
  }
  function handleDragLeave(e: DragEvent) {
    // 패널 밖으로 나갈 때만
    if (e.currentTarget === e.target) setDragOver(false);
  }
  async function handleDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    await uploadAndSend(files);
  }

  // 클립보드 붙여넣기 — textarea 에 paste 시 이미지 자동 업로드
  async function handlePaste(e: ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imgs: File[] = [];
    for (const it of Array.from(items)) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) imgs.push(f);
      }
    }
    if (imgs.length > 0) {
      e.preventDefault();
      await uploadAndSend(imgs);
    }
    // 텍스트 paste 는 기본 동작 그대로
  }

  async function handleIdentify() {
    if (!inquiryId) return;
    if (!identifyName.trim() && !identifyPhone.trim()) {
      setShowIdentify(false);
      return;
    }
    try {
      await api.identify({
        inquiry_id: inquiryId,
        name: identifyName.trim() || undefined,
        phone: identifyPhone.trim() || undefined,
      });
      setIdentified(true);
      setShowIdentify(false);
    } catch (e: any) {
      setError(e?.message || '저장 실패');
    }
  }

  return (
    <div>
      {!open && (
        <button class="asc-launcher" onClick={() => setOpen(true)} aria-label="채팅 열기">
          <svg class="asc-launcher-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 3C6.477 3 2 6.91 2 11.5c0 2.18 1.024 4.158 2.69 5.621-.16.95-.598 2.27-1.39 3.38a.5.5 0 0 0 .58.766c1.62-.483 3.398-1.342 4.62-2.114a11.6 11.6 0 0 0 3.5.547c5.523 0 10-3.91 10-8.5S17.523 3 12 3z" fill="currentColor"/>
          </svg>
          <span class="asc-launcher-textblock">
            <span class="asc-launcher-text">궁금한 건 채팅으로 문의하세요</span>
            <span class="asc-launcher-sub">{cfg.eta}</span>
          </span>
          {unread > 0 && <span class="asc-launcher-badge">{unread}</span>}
        </button>
      )}
      {open && (
        <div
          class={`asc-panel ${dragOver ? 'asc-dragover' : ''}`}
          onDragOver={handleDragOver as any}
          onDragLeave={handleDragLeave as any}
          onDrop={handleDrop as any}
        >
          <div class="asc-head">
            <div>
              <div class="asc-head-title">{cfg.title}</div>
              <div class="asc-head-sub">{cfg.online_hours}</div>
            </div>
            <button class="asc-head-x" onClick={() => setOpen(false)} aria-label="닫기">×</button>
          </div>

          <div class="asc-body" ref={bodyRef as any}>
            <div class="asc-greet">
              <div class="asc-greet-title">{cfg.greeting}</div>
              <div>견적·규격·납기 어떤 거든 편하게 물어봐 주세요.</div>
              <div class="asc-greet-hours">⏰ {cfg.online_hours}</div>
            </div>

            {showIdentify && !identified && (
              <div class="asc-identify">
                <div class="asc-identify-title">연락처를 남겨주세요</div>
                <div class="asc-identify-desc">
                  채팅창을 닫으셔도 답장을 문자로 보내드려요. (개인정보는 상담 답변 알림 목적으로만 사용)
                </div>
                <input
                  type="text"
                  placeholder="이름 (선택)"
                  value={identifyName}
                  onInput={(e) => setIdentifyName((e.currentTarget as HTMLInputElement).value)}
                />
                <input
                  type="tel"
                  placeholder="휴대폰 번호 (예: 010-1234-5678)"
                  value={identifyPhone}
                  onInput={(e) => setIdentifyPhone((e.currentTarget as HTMLInputElement).value)}
                />
                <button class="asc-identify-btn" onClick={handleIdentify}>저장</button>
                <button class="asc-identify-skip" onClick={() => setShowIdentify(false)}>나중에</button>
              </div>
            )}

            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} onImageClick={(u) => setLightboxUrl(u)} />
            ))}
          </div>

          {/* 라이트박스 모달 */}
          {lightboxUrl && (
            <div
              class="asc-lightbox"
              onClick={() => setLightboxUrl(null)}
              role="dialog"
              aria-label="이미지 크게 보기"
            >
              <img src={lightboxUrl} alt="" />
              <button class="asc-lightbox-x" aria-label="닫기">×</button>
            </div>
          )}

          {uploading && <div class="asc-uploading">이미지 업로드 중...</div>}
          {error && <div class="asc-error">⚠ {error}</div>}

          <div class="asc-input">
            <label class="asc-input-attach" title="이미지 첨부">
              📎
              <input type="file" accept="image/*" onChange={handleFile} />
            </label>
            <textarea
              value={text}
              onInput={(e) => setText((e.currentTarget as HTMLTextAreaElement).value)}
              onPaste={handlePaste as any}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="메시지를 입력하세요"
              rows={1}
            />
            <button
              class="asc-input-btn"
              onClick={() => handleSend()}
              disabled={!text.trim() && !uploading}
              aria-label="전송"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ m, onImageClick }: { m: Message; onImageClick: (u: string) => void }) {
  const isMe = m.direction === 'inbound'; // 위젯에선 고객(inbound) = 오른쪽
  const time = m.sent_at ? fmtTime(m.sent_at) : '';
  return (
    <div class={`asc-msg-row ${isMe ? 'me' : 'them'}`}>
      {!isMe && <div class="asc-avatar" aria-hidden>🦊</div>}
      <div class="asc-msg-col">
        <div class="asc-bubble">
          {renderText(m.text, onImageClick)}
          {(m.attachments || []).map((a, i) => (
            <RenderAttachment key={i} url={a.url} onImageClick={onImageClick} />
          ))}
        </div>
        <div class="asc-msg-meta">{isMe ? '나' : '상담사'} · {time}</div>
      </div>
    </div>
  );
}

function RenderAttachment({ url, onImageClick }: { url: string; onImageClick: (u: string) => void }) {
  if (IMG_RE.test(url)) {
    return (
      <img
        class="asc-attach"
        src={url}
        alt="첨부 이미지"
        onClick={(e) => { e.preventDefault(); onImageClick(url); }}
      />
    );
  }
  return (
    <div>
      <a href={url} target="_blank" rel="noopener noreferrer">📎 첨부 파일</a>
    </div>
  );
}

function renderText(text: string, onImageClick: (u: string) => void) {
  // URL 자동 링크화 — split + map
  if (!text) return null;
  const parts: Array<string | { url: string }> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ url: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => {
    if (typeof p === 'string') return <span key={i}>{p}</span>;
    const href = p.url.startsWith('http') ? p.url : 'http://' + p.url;
    // 이미지 URL 이면 인라인 표시
    if (IMG_RE.test(p.url)) {
      return <RenderAttachment key={i} url={href} onImageClick={onImageClick} />;
    }
    return (
      <a key={i} href={href} target="_blank" rel="noopener noreferrer">
        {p.url}
      </a>
    );
  });
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
}
